import {Button, CheckBox, ColorPickerBox, Column, Row, RowLR, Select, Spinner, Text, TextArea, TextInput} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {InfoButton, Link, Observer} from "web-vcore";
import {PropertiesUI_SharedProps} from "../PropertiesUI.js";
import {observer_mgl, Validate} from "mobx-graphlink";
import {ShowMessageBox} from "react-vmessagebox";
import React from "react";
import {store} from "../../../../Store/index.js";
import {MapObject, ShapeType} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {Vec2, Vec2Simple} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {Assert, Clone, CloneWithPrototypes, GetEntries} from "js-vextensions";
import {UpdateMapObject} from "../../../../Server/Commands/MapObjectCommands.js";
import {CloneSafe} from "../../../../Utils/General/Serialization.js";
import {TexturePicker} from "../../../../Utils/ReactComponents/TexturePicker.js";
import {ResolveObjPosInWorld} from "../../../../Server/DB/mapObjects/$mapObject.js";
import {GetPointsMatchingShape} from "../../../../Utils/ThreeJS/ShapeUtils.js";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {GuideShape, ObjMesh} from "../../../../Server/DB/mapObjects/@MapObject/ObjMesh.js";
import {ShowPointsTransformDialog} from "./@Shared/PointsTransformDialog.js";

export const Meshes_CompUI = observer_mgl((props: {} & PropertiesUI_SharedProps)=>{
	const {map, mapState, obj, Change} = props;
	return (
		<Column>
			<Row>
				<Text>Meshes:</Text>
				<Button ml={5} mdIcon="plus" onClick={()=>{
					const newMesh = new ObjMesh({
						texture_size: new Vec2(1, 1),
						points: [[0,0],[0,1],[1,1],[1,0]],
						guide_shape: GuideShape.square,
						guide_size: 1,
						guide_alignPoints: true,
					});
					Change(a=>a.meshes = (a.meshes ?? []).concat(newMesh));
				}}/>
			</Row>
			{(obj.meshes ?? []).map((path, index)=>{
				return <MeshEditorUI key={index} {...{map, mapState, obj, index, Change}}/>;
			})}
		</Column>
	);
});

const MeshEditorUI = observer_mgl((props: {index: number} & PropertiesUI_SharedProps)=>{
	const {map, obj, Change: Change_Outer, index} = props;
	const objPos = ResolveObjPosInWorld(obj);
	const mesh = obj.meshes[index];
	const Change = (objChanger: (obj: ObjMesh)=>any)=>{
		return Change_Outer(a=>objChanger(a.meshes[index]));
	};
	const changeRot = (delta: number)=>{
		Change(newData=>{
			const newRot = (newData.texture_rotation ?? 0) + delta;
			newData.texture_rotation = newRot % 360;
			if (newData.texture_rotation < 0) newData.texture_rotation += 360;
		});
	};

	return (
		<Column mt={5} style={{background: "rgba(255,255,255,.2)", padding: 5, borderRadius: 5}}>
			<Row>
				<Text style={{minWidth: 10}}>Mesh {index + 1}</Text>
				<Row ml="auto">
					<CheckBox text="Selected" value={mesh.selected ?? false} onChange={val=>{
						Change_Outer(a=>{
							a.meshes = a.meshes.map((meshInClone, i)=>{
								if (i == index) meshInClone.selected = val;
								else meshInClone.selected = false; // unselect others
								return meshInClone;
							});
						});
					}}/>
					<Button ml={5} mdIcon="content-copy" title="Clone" onClick={()=>{
						Change_Outer(a=>{
							const thisMesh_new = CloneSafe(a.meshes[index]);
							const thisMesh_clone = CloneSafe(thisMesh_new);
							a.meshes[index] = thisMesh_new;
							a.meshes.Insert(index + 1, thisMesh_clone);
							// set only the new clone as selected
							for (const mesh2 of a.meshes) mesh2.selected = false;
							thisMesh_clone.selected = true;
						});
					}}/>
					<Button ml={5} mdIcon="delete" onClick={()=>{
						ShowMessageBox({
							title: "Delete mesh?", cancelButton: true,
							message: "Are you sure you want to delete this mesh?",
							onOK: ()=>{
								Change_Outer(a=>a.meshes.RemoveAt(index));
							},
						});
					}}/>
				</Row>
			</Row>
			<Row mt={5}>
				<Text>Color:</Text>
				<ColorPickerBox ml={5} color={(mesh.color ?? [255,255,255]) as [number,number,number]} onChange={val=>Change(a=>a.color = val)}/>
				<InfoButton ml={5} text={`
					If set, and there's no texture, this color will be used as the mesh's color.
					If set, and the mesh also has a texture, this color will be used to colorize it. (with the alpha controlling the colorization intensity)
					Note: When meshes are assigned a color, they end up showing a bit darker in-map than that color would normally appear, eg. on html elements. (eg. mesh's [43,43,43] is equivalent to css' [25,25,25])
					Key colors (in mesh/in-map color-space): Map background is [43,43,43].
				`.AsMultiline(0)}/>
				<Text ml={5}>Opacity:</Text>
				<SpinnerForPercent ml={5} style={{width: 50}} value={mesh.opacity ?? 1} onChange={val=>Change(a=>a.opacity = val)} title="Opacity of the mesh. (for colorization intensity, instead set color's alpha)"/>
				<CheckBox ml={5} text="Render" value={!mesh.renderDisabled} onChange={val=>{
					Change(a=>a.renderDisabled = val ? undefined : true);
				}}/>
			</Row>
			<Row mt={5}>
				<Text>Texture:</Text>
				<TexturePicker ml={5} value={mesh.texture_id} onChange={val=>Change(a=>a.texture_id = val)}/>
				<Column ml={5} style={{flex: 1}}>
					<Row style={{flex: 1}}>
						<Text>Size, X (m):</Text>
						<Spinner ml={5} min={0} step={0.1} style={{width: 50}} value={mesh.texture_size?.x ?? 0} onChange={val=>Change(a=>a.texture_size = new Vec2(val, a.texture_size?.y ?? 0))}/>
						<Text ml={5}>Y (m):</Text>
						<Spinner ml={5} min={0} step={0.1} style={{width: 50}} value={mesh.texture_size?.y ?? 0} onChange={val=>Change(a=>a.texture_size = new Vec2(a.texture_size?.x ?? 0, val))}/>
					</Row>
					<Row style={{flex: 1}}>
						<Text>Rotation:</Text>
						<Spinner ml={5} step="any" style={{width: 50}} value={mesh.texture_rotation ?? 0} onChange={val=>Change(a=>a.texture_rotation = val)}/>
						<Button ml={5} p="2px 5px" text="-90" onClick={()=>changeRot(-90)}/>
						<Button ml={5} p="2px 5px" text="+90" onClick={()=>changeRot(90)}/>
						<Button ml={5} p="2px 5px" text="Fix" onClick={()=>changeRot(0)}/>
					</Row>
				</Column>
			</Row>
			<Row mt={5}>
				<Text>Guide, shape:</Text>
				<Select ml={5} options={[{name: "None", value: null}, ...GetEntries(GuideShape, "ui")]} value={mesh.guide_shape} onChange={val=>Change(a=>a.guide_shape = val)}/>
				<Text ml={5}>Size:</Text>
				<Spinner ml={5} min={0} step={0.1} value={mesh.guide_size ?? 0} onChange={val=>Change(a=>a.guide_size = val)}/>
				<CheckBox ml={5} text="Align points" value={mesh.guide_alignPoints ?? false} onChange={val=>Change(a=>a.guide_alignPoints = val)}/>
			</Row>
			<Row mt={5}>
				<Text>Z offset:</Text>
				<Spinner ml={5} min={Number.MIN_SAFE_INTEGER} step="any" value={mesh.zOffset ?? 0} onChange={val=>Change(a=>a.zOffset = val)}/>
				<Text ml={5}>(world: {objPos.z + (mesh.zOffset ?? 0)})</Text>
			</Row>
			<Row mt={5}>
				<Text>Points:</Text>
				<Button ml="auto" text="Transforms" onClick={()=>{
					ShowPointsTransformDialog(mesh.points);
				}}/>
				<Button ml={5} mdIcon="square-outline" title="Reset (to 1x1 square)" onClick={()=>{
					ShowMessageBox({
						title: "Reset points, to square?", cancelButton: true,
						message: "Are you sure you want to reset the points to a simple square?",
						onOK: ()=>{
							const newMeshes = CloneSafe(obj.meshes);
							const squareSize = obj.meshes[index].guide_size ?? 1;
							newMeshes[index].points = [[0, 0], [0, squareSize], [squareSize, squareSize], [squareSize, 0]];
							new UpdateMapObject({
								id: obj.id,
								updates: {meshes: newMeshes},
							}).RunOnServer();
						},
					});
				}}/>
				<Button ml={5} mdIcon="circle" title="Reset to ellipse" onClick={()=>{
					let pointCount = 16;
					const ChangeForDialog = (..._)=>boxController.UpdateUI();
					const boxController = ShowMessageBox({
						title: "Reset points to ellipse?", cancelButton: true,
						message: ()=>{
							return (
								<Column>
									<Text>Reset mesh to an ellipse, filling the shape-rect area?</Text>
									<Row>
										<Text>Point count:</Text>
										<Spinner ml={5} min={3} step={1} value={pointCount} onChange={val=>ChangeForDialog(pointCount = val)}/>
									</Row>
								</Column>
							);
						},
						onOK: ()=>{
							const newMeshes = CloneSafe(obj.meshes);
							newMeshes[index].points = GetPointsMatchingObjShape(obj.shape_size, ShapeType.ellipse, pointCount);
							new UpdateMapObject({
								id: obj.id,
								updates: {meshes: newMeshes},
							}).RunOnServer();
						},
					});
				}}/>
				<Button ml={5} mdIcon="square" title="Reset to rect" onClick={()=>{
					ShowMessageBox({
						title: "Reset mesh to rect?", cancelButton: true,
						message: "Reset mesh to a rectangle, filling the shape-rect area?",
						onOK: ()=>{
							const newMeshes = CloneSafe(obj.meshes);
							newMeshes[index].points = GetPointsMatchingObjShape(obj.shape_size, ShapeType.rectangle);
							new UpdateMapObject({
								id: obj.id,
								updates: {meshes: newMeshes},
							}).RunOnServer();
						},
					});
				}}/>
			</Row>
			<TextArea autoSize={true} value={JSON.stringify(mesh.points)} onChange={val=>Change(a=>a.points = JSON.parse(val))}/>
		</Column>
	);
});

/** Backward-compatible wrapper: the pure footprint math now lives in `ShapeUtils` (Buildings Set1
 *  §9 refactor note), which the DB-layer accessors import directly (avoiding a UI import cycle).
 *  This keeps existing UI call sites unchanged. */
export function GetPointsMatchingObjShape(shape_size: Vec2|n, shape_type: ShapeType, circle_pointCount = 16): Vec2Simple[] {
	const size = shape_size ? [shape_size.x, shape_size.y] as [number, number] : null;
	return GetPointsMatchingShape(size, shape_type as string as "ellipse"|"rectangle", circle_pointCount);
}

/*export function PointsToSimpleJSON(points: Vec2[]) {
	return JSON.stringify(points.map(a=>[a.x, a.y]));
}
export function SimpleJSONToPoints(pointsJSON: string) {
	return JSON.parse(pointsJSON).map((a: number[])=>new Vec2(a[0], a[1]));
}*/